export interface SourcesResponse {
  id: string;
  code: string;
  name: string;
  transcriptType: string;
  transcriptMetadataTypeId: number;
  step: string;
  stepId: string;
}

export interface ITPIntegrationSourceCreate {
  name: string;
  integrationStepId: string;
  code: string;
  actionType: number;
}

export class ITPIntegrationSourceSdk {
  private baseURL: string;

  /**
   * Constructor
   * @param {string} baseURL - The base URL for the service
   */
  constructor(baseURL: string) {
    if (!baseURL || baseURL.trim() === '') {
      throw new Error('baseURL is required and cannot be empty');
    }

    try {
      new URL(baseURL);
    } catch (error) {
      throw new Error('baseURL must be a valid URL');
    }

    this.baseURL = baseURL;
  }

  /**
   * Wrapper for handling fetch requests and error handling
   * @param {string} endpoint - API endpoint
   * @param {RequestInit} [options] - Request options
   * @returns {Promise<Response>} - Response or throws an error
   */
  private async fetchWrapper(endpoint: string, options?: RequestInit): Promise<Response> {
    const url = `${this.baseURL}${endpoint}`;
    const response = await fetch(url, options);

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage += `, message: ${errorData.message}`;
        }
      } catch (e) {
        // ignore
      }
      throw new Error(errorMessage);
    }

    return response;
  }

  /**
   * Get all ITP Integration Sources for tenant
   * @param {string} accessToken - user's access token
   * @returns {Promise<SourcesResponse[]>} - List of sources
   */
  async getITPIntegrationSource(accessToken: string): Promise<SourcesResponse[]> {
    const endpoint = `/itp/integration/sources`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Get ITP Integration Sources by transcript type
   * @param {string} accessToken - user's access token
   * @param {number} transcriptMetadataTypeId - transcript type ID
   * @returns {Promise<SourcesResponse[]>} - List of sources
   */
  async getITPIntegrationSourceByTranscriptType(
    accessToken: string,
    transcriptMetadataTypeId: number
  ): Promise<SourcesResponse[]> {
    const endpoint = `/itp/integration/sources/getByTranscriptType/${transcriptMetadataTypeId}`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.json();
  }

  /**
   * Create a new ITP Integration Source
   * @param {string} accessToken - user's access token
   * @param {ITPIntegrationSourceCreate} data - source data to create
   * @returns {Promise<ITPIntegrationSourceCreate>} - created source
   */
  async addITPIntegrationSource(
    accessToken: string,
    data: ITPIntegrationSourceCreate
  ): Promise<ITPIntegrationSourceCreate> {
    const endpoint = `/itp/integration/sources`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    return response.json();
  }

  /**
   * Update an existing ITP Integration Source
   * @param {string} accessToken - user's access token
   * @param {string} id - source ID to update
   * @param {ITPIntegrationSourceCreate} data - source data to update
   * @returns {Promise<ITPIntegrationSourceCreate & { id: string }>} - updated source
   */
  async updateITPIntegrationSource(
    accessToken: string,
    id: string,
    data: ITPIntegrationSourceCreate
  ): Promise<ITPIntegrationSourceCreate> {
    const endpoint = `/itp/integration/sources/${id}`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    return response.json();
  }

  /**
   * Delete an ITP Integration Source
   * @param {string} accessToken - user's access token
   * @param {string} id - source ID to delete
   * @returns {Promise<string>} - deletion confirmation message
   */
  async deleteITPIntegrationSource(accessToken: string, id: string): Promise<string> {
    const endpoint = `/itp/integration/sources/${id}`;

    const response = await this.fetchWrapper(endpoint, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    return response.text();
  }
}
